import { Box } from "@mui/material";
import { Nullable, formatCurrencyFromCents, AvailablePaymentCurrency } from "@simplyk/common";
import { useController, UseFormReturn } from "react-hook-form";

import { deriveDisplayUnitAmount } from "./deriveDisplayUnitAmount";

import { useLocaleField } from "@/canvas/features/FormV2/helpers/useLocaleField";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { propagateShopPwycUnitToTickets } from "@/common/helpers/shopVariants";
import { isMin } from "@/common/helpers/validators";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { RateOutput } from "@/common/types/trpc";
import { FormAmountField } from "@/forms-design-system/FormAmountField";
import { FormTooltip } from "@/forms-design-system/FormTooltip";

export type ShopPayWhatYouCanInputProps = {
  parentRate: RateOutput;
  parentTicketIndex: number;
  cartQuantity: number;
  minimumAmountPerTicket?: Nullable<number>;
  suggestedAmountPerTicket?: Nullable<number>;
  disabled?: boolean;
  lockedTooltipText?: string | null;
  form: UseFormReturn<TicketingPaymentInput>;
  formCurrency: AvailablePaymentCurrency;
};

export const ShopPayWhatYouCanInput = ({
  parentRate,
  parentTicketIndex,
  cartQuantity,
  minimumAmountPerTicket,
  suggestedAmountPerTicket,
  disabled,
  lockedTooltipText,
  form,
  formCurrency,
}: ShopPayWhatYouCanInputProps) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();
  const getLocaleField = useLocaleField();
  const rateTitle = getLocaleField(parentRate.rateFields)?.title;

  const { control, register } = form;
  const { errors } = form.formState;

  const formattedMinimumAmount = minimumAmountPerTicket
    ? formatCurrencyFromCents(minimumAmountPerTicket, { isoLocale, currency: formCurrency })
    : undefined;

  const formattedSuggestedAmount = suggestedAmountPerTicket
    ? formatCurrencyFromCents(suggestedAmountPerTicket, { isoLocale, currency: formCurrency })
    : undefined;

  const parentRowQuantity = form.watch(`ticketsPurchased.${parentTicketIndex}.purchasedNumber`) ?? 0;
  const isInCart = cartQuantity > 0 || parentRowQuantity > 0;

  const {
    field: { ref: unitAmountFieldRef, value: unitAmountValue, onChange: onUnitAmountChange, ...unitAmountField },
  } = useController<TicketingPaymentInput, `ticketsPurchased.${number}.pwycAmount`>({
    control,
    name: `ticketsPurchased.${parentTicketIndex}.pwycAmount`,
    rules: minimumAmountPerTicket
      ? {
          validate: {
            required: (value) =>
              !isInCart || (value !== null && value !== undefined)
                ? true
                : t("ticketing", "payWhatYouCanInputRequired"),
            isMin: (value) => {
              if (!isInCart || value === undefined || value === null) {
                return true;
              }
              const unitAmount = deriveDisplayUnitAmount(Number(value), parentRowQuantity);
              return unitAmount >= minimumAmountPerTicket
                ? true
                : t("ticketing", "payWhatYouCanInputMinError_OneTicket", { amount: formattedMinimumAmount });
            },
          },
        }
      : {
          ...isMin(0, t("ticketing", "payWhatYouCanInputNegativeError")),
        },
  });

  const handleChange = (cents: number | null) => {
    onUnitAmountChange(cents);
    const tickets = form.getValues("ticketsPurchased");
    const updates = propagateShopPwycUnitToTickets({
      parentRate,
      ticketsPurchased: tickets,
      unitAmount: cents,
    });
    for (const update of updates) {
      form.setValue(`ticketsPurchased.${update.index}.pwycAmount`, update.pwycAmount, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  };

  const getPlaceholder = () => {
    if (suggestedAmountPerTicket) {
      return t("ticketing", "payWhatYouCanInputPlaceholderSuggested_OneTicket", {
        amount: formattedSuggestedAmount,
      });
    }
    if (minimumAmountPerTicket) {
      return t("ticketing", "payWhatYouCanInputPlaceholderMinimum_OneTicket", {
        amount: formattedMinimumAmount,
      });
    }
    return t("ticketing", "payWhatYouCanInputPlaceholder");
  };

  const fieldError = errors?.ticketsPurchased?.[parentTicketIndex]?.pwycAmount?.message;
  const rawPwycAmount = unitAmountValue ?? 0;
  const displayUnitAmount = deriveDisplayUnitAmount(rawPwycAmount, parentRowQuantity);
  const totalAmount = displayUnitAmount * cartQuantity;
  const showTotalCaption = cartQuantity > 1 && displayUnitAmount > 0;
  const showLockedTooltip = Boolean(disabled && lockedTooltipText);
  const fieldValue = unitAmountValue === null || unitAmountValue === undefined ? null : displayUnitAmount;

  const helperCaption = fieldError
    ? fieldError
    : showTotalCaption
      ? t("ticketing", "payWhatYouCanInputShopTotalLabel", {
          amount: formatCurrencyFromCents(totalAmount, { isoLocale, currency: formCurrency }),
          count: cartQuantity,
        })
      : t("ticketing", "shopPwycInputAmountIsPerItem");

  const amountField = (
    <FormAmountField
      {...unitAmountField}
      inputRef={unitAmountFieldRef}
      placeholder={getPlaceholder()}
      value={fieldValue}
      onChange={handleChange}
      disabled={disabled}
      error={!!fieldError}
      helperText={helperCaption}
      currency={formCurrency}
      inputProps={rateTitle ? { "aria-label": t("common", "a11y.amountFor", { item: rateTitle }) } : undefined}
      data-test="shop-pwyc-amount-input"
    />
  );

  return (
    <>
      {showLockedTooltip ? (
        <FormTooltip title={lockedTooltipText} placement="top" maxWidth="280px">
          <Box sx={{ opacity: 0.4, cursor: "not-allowed" }} data-test="shop-pwyc-amount-input-locked-wrapper">
            {amountField}
          </Box>
        </FormTooltip>
      ) : (
        amountField
      )}
      <input
        type="hidden"
        value={parentRate.id || undefined}
        {...register(`ticketsPurchased.${parentTicketIndex}.rateId`)}
        readOnly
      />
    </>
  );
};
